/**
 * Complementary Products Navigation
 * Adds arrow navigation and dot pagination functionality
 * Works with both Flickity slider mode and native scroll mode
 */

(function() {
  'use strict';

  class ComplementaryProductsNavigation {
    constructor() {
      this.initialized = new Set(); // Track initialized blocks
      this.init();
    }

    init() {
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setupAll());
      } else {
        // DOM already ready, setup immediately
        this.setupAll();
      }

      // Listen for dynamically loaded complementary products
      document.addEventListener('theme:complementary:loaded', (e) => {
        const container = e.detail.container;
        // Wait a bit for the DOM to be fully updated
        setTimeout(() => {
          this.setupNavigation(container);
        }, 300);
      });
    }

    setupAll() {
      // Setup navigation for all complementary product blocks
      const blocks = document.querySelectorAll('.product__complementary');

      blocks.forEach(block => {
        const complementaryProducts = block.querySelector('complementary-products');
        if (complementaryProducts) {
          // Check if products are already loaded
          const hasProducts = complementaryProducts.querySelector('quick-add-product');

          if (hasProducts) {
            // Products already loaded, setup immediately
            this.setupNavigation(complementaryProducts);
          } else {
            // Products not loaded yet, wait for them
            this.waitForProducts(complementaryProducts);
          }
        }
      });
    }

    waitForProducts(complementaryProductsEl) {
      // Use MutationObserver to detect when products are added
      const observer = new MutationObserver(() => {
        const hasProducts = complementaryProductsEl.querySelector('quick-add-product');

        if (hasProducts) {
          observer.disconnect();
          // Products found, setup navigation
          setTimeout(() => {
            this.setupNavigation(complementaryProductsEl);
          }, 100);
        }
      });

      observer.observe(complementaryProductsEl, {
        childList: true,
        subtree: true
      });

      // Fallback: stop observing after 5 seconds
      setTimeout(() => {
        observer.disconnect();
      }, 5000);
    }

    setupNavigation(complementaryProductsEl) {
      const block = complementaryProductsEl.closest('.product__complementary');
      if (!block) return;

      // Check if already initialized
      const blockId = block.getAttribute('data-shopify-editor-block') || 'default';
      if (this.initialized.has(blockId)) {
        return;
      }
      this.initialized.add(blockId);

      const prevBtn = block.querySelector('.complementary-products__arrow--prev');
      const nextBtn = block.querySelector('.complementary-products__arrow--next');
      const dotsContainer = block.querySelector('.complementary-products__dots');

      if (!prevBtn || !nextBtn || !dotsContainer) return;

      // Check if slider mode is enabled
      const sliderComponent = complementaryProductsEl.querySelector('slider-component');

      if (sliderComponent) {
        // Slider mode with Flickity
        this.setupFlickityNavigation(sliderComponent, prevBtn, nextBtn, dotsContainer);
      } else {
        // Native scroll mode
        this.setupScrollNavigation(complementaryProductsEl, prevBtn, nextBtn, dotsContainer);
      }
    }

    setupFlickityNavigation(sliderComponent, prevBtn, nextBtn, dotsContainer) {
      // Wait for Flickity to initialize
      const checkFlickity = () => {
        const flickityEl = sliderComponent.querySelector('.flickity-enabled');

        if (flickityEl && flickityEl.flickityInstance) {
          const flkty = flickityEl.flickityInstance;

          // Create dots
          this.createFlickityDots(flkty, dotsContainer);

          // Update navigation state
          const updateNav = () => {
            prevBtn.disabled = flkty.selectedIndex === 0;
            nextBtn.disabled = flkty.selectedIndex === flkty.slides.length - 1;

            // Update active dot
            const dots = dotsContainer.querySelectorAll('.complementary-products__dot');
            dots.forEach((dot, index) => {
              dot.classList.toggle('is-active', index === flkty.selectedIndex);
            });
          };

          // Arrow click handlers
          prevBtn.addEventListener('click', () => flkty.previous());
          nextBtn.addEventListener('click', () => flkty.next());

          // Listen to Flickity changes
          flkty.on('change', updateNav);
          flkty.on('select', updateNav);

          // Initial update
          updateNav();

        } else {
          // Retry after a short delay
          setTimeout(checkFlickity, 100);
        }
      };

      checkFlickity();
    }

    createFlickityDots(flkty, dotsContainer) {
      dotsContainer.innerHTML = '';

      flkty.slides.forEach((_, index) => {
        const dot = document.createElement('button');
        dot.classList.add('complementary-products__dot');
        dot.setAttribute('type', 'button');
        dot.setAttribute('aria-label', `Go to slide ${index + 1}`);

        if (index === 0) {
          dot.classList.add('is-active');
        }

        dot.addEventListener('click', () => {
          flkty.select(index);
        });

        dotsContainer.appendChild(dot);
      });
    }

    setupScrollNavigation(complementaryProductsEl, prevBtn, nextBtn, dotsContainer) {
      // Get all product items
      const items = Array.from(complementaryProductsEl.querySelectorAll('quick-add-product'));

      if (items.length === 0) {
        console.warn('ComplementaryNavigation: No products found');
        return;
      }

      let currentIndex = 0;
      let itemsPerView = 1;

      // Calculate how many items are visible at once
      const calculateItemsPerView = () => {
        if (items.length === 0) return;

        const containerWidth = complementaryProductsEl.offsetWidth;
        const itemWidth = items[0].offsetWidth;
        const gap = 16; // from CSS

        itemsPerView = Math.floor(containerWidth / (itemWidth + gap)) || 1;
        return itemsPerView;
      };

      // Create dots
      const createDots = () => {
        const totalCards = items.length;

        dotsContainer.innerHTML = '';

        // Créer un dot par carte
        for (let i = 0; i < totalCards; i++) {
          const dot = document.createElement('button');
          dot.classList.add('complementary-products__dot');
          dot.setAttribute('type', 'button');
          dot.setAttribute('aria-label', `Go to product ${i + 1}`);

          if (i === 0) {
            dot.classList.add('is-active');
          }

          dot.addEventListener('click', () => {
            goToCard(i);
          });

          dotsContainer.appendChild(dot);
        }

        updateNavigation();
      };

      // Navigate to a specific card by index
      const goToCard = (cardIndex) => {
        if (items.length === 0 || cardIndex < 0 || cardIndex >= items.length) return;

        currentIndex = cardIndex;
        const item = items[cardIndex];

        // Calculer la position de scroll pour cette carte
        const scrollLeft = item.offsetLeft - complementaryProductsEl.offsetLeft;

        complementaryProductsEl.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        });

        updateNavigation();
      };

      // Navigate to a specific page (alias pour goToCard)
      const goToPage = (pageIndex) => {
        goToCard(pageIndex);
      };

      // Update button states and active dot
      const updateNavigation = () => {
        const scrollLeft = complementaryProductsEl.scrollLeft;
        const scrollMax = complementaryProductsEl.scrollWidth - complementaryProductsEl.offsetWidth;

        // Disable arrows aux limites
        prevBtn.disabled = scrollLeft <= 0;
        nextBtn.disabled = scrollLeft >= scrollMax - 1;

        // Trouver quelle carte est la plus proche de la position actuelle
        let closestIndex = 0;
        let minDistance = Infinity;

        items.forEach((item, index) => {
          const itemLeft = item.offsetLeft - complementaryProductsEl.offsetLeft;
          const distance = Math.abs(scrollLeft - itemLeft);

          if (distance < minDistance) {
            minDistance = distance;
            closestIndex = index;
          }
        });

        currentIndex = closestIndex;

        // Update dots
        const dots = dotsContainer.querySelectorAll('.complementary-products__dot');
        dots.forEach((dot, index) => {
          dot.classList.toggle('is-active', index === currentIndex);
        });
      };

      // Handle scroll event to update current index
      let scrollTimeout;
      complementaryProductsEl.addEventListener('scroll', () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          updateNavigation(); // updateNavigation calcule maintenant currentIndex
        }, 100);
      });

      // Arrow navigation
      prevBtn.addEventListener('click', () => {
        if (currentIndex > 0) {
          goToCard(currentIndex - 1);
        }
      });

      nextBtn.addEventListener('click', () => {
        if (currentIndex < items.length - 1) {
          goToCard(currentIndex + 1);
        }
      });

      // Handle window resize
      let resizeTimeout;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          createDots();
        }, 250);
      });

      // Initialize - wait a bit to ensure layout is stable
      setTimeout(() => {
        createDots();
      }, 200);
    }
  }

  // Initialize when ready
  new ComplementaryProductsNavigation();

})();
